import { useState } from 'react';
import { Table, Button, Typography, Space, Popconfirm, App, Tag } from 'antd';
import { UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import type { SpecDocMeta } from '@bom/shared';
import { useSessionStore } from '../../stores/sessionStore';
import { useSpecs, useDeleteSpec, openSpecFile } from '../../api/specs';
import { NoTenant } from '../../components/NoTenant';
import { fmtTime } from '../../lib/format';
import { LIST_PAGINATION, LIST_SCROLL_Y } from '../../lib/tablePagination';
import { SpecUploadModal } from './SpecUploadModal';

/**
 * 规格书 Tab：平台级按料号归档、跨产品共享；多次上传即更新时间线；可下载、可删除。
 * 本产品 BOM 含该料号即显示（含其他产品上传的）；删除对全平台生效。不发通知邮件。
 */
export function SpecListPage({ partOptions }: { partOptions?: { value: string }[] }) {
  const { message } = App.useApp();
  const tid = useSessionStore((s) => s.currentTenant?.id);
  const role = useSessionStore((s) => s.role);
  const isPlatformAdmin = useSessionStore((s) => s.user?.is_platform_admin ?? false);
  const canUpload = isPlatformAdmin || role === 'admin' || role === 'editor';
  const canDelete = isPlatformAdmin || role === 'admin';

  const specs = useSpecs(tid);
  const del = useDeleteSpec(tid ?? '');
  const [uploadOpen, setUploadOpen] = useState(false);

  if (!tid) return <NoTenant />;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <Typography.Text type="secondary">
          规格书按料号归档、跨产品共享 —— 本产品 BOM
          含该料号即显示（含其他产品上传的）；同一料号多次上传即形成「更新时间线」。不发送邮件。
        </Typography.Text>
        {canUpload && (
          <Button type="primary" icon={<UploadOutlined />} onClick={() => setUploadOpen(true)}>
            上传规格书
          </Button>
        )}
      </Space>

      <Table<SpecDocMeta>
        rowKey="id"
        loading={specs.isLoading}
        dataSource={specs.data ?? []}
        scroll={{ x: 'max-content', y: LIST_SCROLL_Y }}
        pagination={LIST_PAGINATION}
        columns={[
          {
            title: '料号',
            dataIndex: 'part_no',
            width: 220,
            render: (_: string, r: SpecDocMeta) => (
              <Space size={4} wrap>
                {(r.part_nos?.length ? r.part_nos : [r.part_no]).map((pn) => (
                  <Tag key={pn} style={{ marginInlineEnd: 0 }}>
                    {pn}
                  </Tag>
                ))}
              </Space>
            ),
          },
          { title: '文件名', dataIndex: 'filename', ellipsis: true },
          {
            title: '版本说明',
            dataIndex: 'note',
            width: 220,
            ellipsis: true,
            render: (v?: string) => v ?? '—',
          },
          {
            title: '来源产品',
            dataIndex: 'source_tenant_name',
            width: 150,
            ellipsis: true,
            render: (v: string | undefined, r: SpecDocMeta) =>
              !r.source_tenant_id || r.source_tenant_id === tid ? (
                <Tag color="blue">本产品</Tag>
              ) : (
                (v ?? '其他产品')
              ),
          },
          { title: '更新时间', dataIndex: 'uploaded_at', width: 170, render: fmtTime },
          {
            title: '操作',
            key: 'action',
            width: 150,
            fixed: 'right',
            render: (_: unknown, r: SpecDocMeta) => (
              <Space size="small">
                <Button
                  size="small"
                  type="link"
                  icon={<DownloadOutlined />}
                  onClick={() =>
                    openSpecFile(tid, r.id, r.part_no).catch(() => message.error('下载失败'))
                  }
                >
                  下载
                </Button>
                {canDelete && (
                  <Popconfirm
                    title="删除该规格书存档？"
                    description="规格书跨产品共享，删除后其所有归属料号、所有产品均不可见"
                    okText="删除"
                    okButtonProps={{ danger: true }}
                    cancelText="取消"
                    onConfirm={() =>
                      del.mutate(
                        { id: r.id, part_no: r.part_no },
                        {
                          onSuccess: () => message.success('已删除'),
                          onError: (e) => message.error((e as Error).message || '删除失败'),
                        },
                      )
                    }
                  >
                    <Button size="small" type="link" danger>
                      删除
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]}
      />

      {uploadOpen && (
        <SpecUploadModal
          tid={tid}
          open={uploadOpen}
          {...(partOptions ? { partOptions } : {})}
          onClose={() => setUploadOpen(false)}
        />
      )}
    </Space>
  );
}
